import { DetailsChip } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DetailsChip.tsx';
import { DetailsTileStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/DetailsTile.styles.tsx';
import { InvestorDetailsRow } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Investors/InvestorDetailsV2/components/InvestorDetailsRow/InvestorDetailsRow.tsx';
import { PhoneNumber } from '@dealstack/frontend/shared/components/PhoneNumber/PhoneNumber';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme.ts';
import {
	getCountryNameByCode,
	getNationalityNamesByCodes,
} from '@dealstack/shared/lib/countries/countriesData';
import { DATE_FORMATS, formatBirthday } from '@dealstack/shared/lib/format/dates/date.ts';
import { GetPersonByIdRes } from '@dealstack/shared/lib/generated';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds.ts';
import { Divider, Flex, Text } from '@mantine/core';

interface Props {
	person: GetPersonByIdRes;
}

/** Who the reviewer is deciding about: contact details, then the identity facts a bank
 *  account is checked against. */
export const ParticipantSummaryCard = ({ person }: Props) => {
	const tMipManage = useTranslationMipManage();

	const dateOfBirth = formatBirthday(person.dateOfBirth, DATE_FORMATS.CALENDAR_DATE);
	const countryOfBirth = getCountryNameByCode(person.countryOfBirth);
	const nationalities = getNationalityNamesByCodes(person.citizenship);

	const hasIdentity =
		!!dateOfBirth || !!countryOfBirth || !!person.placeOfBirth || nationalities.length > 0;

	return (
		<DetailsTileStyles
			$bg={defaultTheme.v3.colors.gray[0]}
			data-testid={TEST_ELEMENT_IDS.MIP_MANAGE_BANK_ACCOUNT_PARTICIPANT_CARD}
		>
			<Flex direction="column" gap="0.25rem">
				<Text fz={12} c="dark.0">
					{tMipManage('bank_accounts.details.participant.contact', {
						defaultValue: 'Contact',
					})}
				</Text>

				<Text fz={14} fw={600} c="dark.8">
					{person.displayName}
				</Text>

				{!!person.email && (
					<Text fz={14} c="dark.8" style={{ wordBreak: 'break-word' }}>
						{person.email}
					</Text>
				)}

				{!!person.telephonePrefix && !!person.phoneNumber && (
					<Text fz={14} c="dark.8">
						<PhoneNumber
							telephonePrefix={person.telephonePrefix}
							phoneNumber={person.phoneNumber}
						/>
					</Text>
				)}
			</Flex>

			{hasIdentity && (
				<>
					<Divider my="0.5rem" />

					<Flex direction="column" gap="0.25rem">
						<Text fz={12} c="dark.0">
							{tMipManage('bank_accounts.details.participant.identity', {
								defaultValue: 'Identity',
							})}
						</Text>

						<InvestorDetailsRow
							label={tMipManage('investors.expanded_row.date_of_birth')}
							value={dateOfBirth}
							padding="0"
						/>

						<InvestorDetailsRow
							label={tMipManage('investors.expanded_row.country_of_birth')}
							value={countryOfBirth}
							padding="0"
						/>

						<InvestorDetailsRow
							label={tMipManage('investors.expanded_row.place_of_birth')}
							value={person.placeOfBirth}
							padding="0"
						/>

						<InvestorDetailsRow
							label={tMipManage('investors.expanded_row.citizenship')}
							value={
								nationalities.length > 0
									? nationalities.map((nationality) => (
											<DetailsChip key={nationality} label={nationality} />
										))
									: undefined
							}
							padding="0"
						/>
					</Flex>
				</>
			)}
		</DetailsTileStyles>
	);
};
